/* eslint-disable complexity */

import {
    xhrDeleteAsync,
    xhrGetAsync,
    xhrGetPaginatedAsync,
    xhrPatchAsync,
    xhrPostAsync,
    xhrPutAsync,
} from '~/libs/core'

import {
    ENGAGEMENTS_API_URL,
    ENGAGEMENTS_ROOT_API_URL,
} from '../constants'
import {
    ApprovedTimesheetListResponse,
    ApprovedTimesheetQuery,
    AssignEngagementManagerPayload,
    Assignment,
    Engagement,
    EngagementFilters,
    EngagementManager,
    PaginationModel,
    Skill,
    TimesheetPaymentEntry,
    TimesheetPaymentSummary,
} from '../models'
import {
    fromEngagementAnticipatedStartApi,
    fromEngagementDateInputValue,
    normalizeEngagement,
    toEngagementAnticipatedStartApi,
    toEngagementRoleApi,
    toEngagementRoleLevelApi,
    toEngagementStatusApi,
    toEngagementWorkloadApi,
} from '../utils'

import {
    fetchSkillsByIds,
} from './skills.service'
import {
    fetchProjectById,
} from './projects.service'

interface BackendMeta {
    page?: number
    perPage?: number
    totalCount?: number
    totalPages?: number
}

interface BackendPaginatedResponse<T> {
    data?: T[]
    meta?: BackendMeta
}

interface AssignmentDetails {
    agreementRate?: string
    candidateWiproId?: string
    durationMonths?: number | string
    endDate?: string
    memberHandle?: string
    memberId?: number | string
    otherRemarks?: string
    paymentCycle?: string
    ratePerHour?: string
    source?: string
    startDate?: string
    standardHoursPerDay?: number | string
    standardHoursPerWeek?: number | string
    totalHours?: number | string
    wiproIdEndDate?: string
}

interface EngagementUpsertData extends Partial<Engagement> {
    assignmentDetails?: AssignmentDetails[]
}

interface EngagementResponse {
    data: Engagement[] | BackendPaginatedResponse<Engagement>
    page: number
    perPage: number
    total: number
    totalPages: number
}

export interface FetchEngagementsParams {
    page?: number
    perPage?: number
}

export interface FetchEngagementsResponse {
    data: Engagement[]
    metadata: PaginationModel
}

export interface EngagementFeedback {
    createdAt: string
    feedbackText: string
    givenByEmail?: string
    givenByHandle?: string
    id: number | string
    rating?: number
    updatedAt: string
}

export interface CreateEngagementFeedbackPayload {
    feedbackText: string
    rating?: number
}

export interface FeedbackLinkPayload {
    customerEmail: string
}

export interface MemberExperience {
    createdAt: string
    experienceText: string
    id: number | string
    memberHandle?: string
    memberId?: number | string
    updatedAt: string
}

export interface CreateMemberExperiencePayload {
    experienceText: string
}

function normalizeError(error: unknown, fallbackMessage: string): Error {
    const typedError = error as {
        message?: string
        response?: {
            data?: {
                message?: string
            }
        }
    }

    const message = typedError?.response?.data?.message
        || typedError?.message
        || fallbackMessage

    return new Error(message)
}

function normalizePaginatedResponse(
    response: EngagementResponse,
    fallbackPage?: number,
    fallbackPerPage?: number,
): {
    data: Engagement[]
    page: number
    perPage: number
    total: number
    totalPages: number
} {
    if (Array.isArray(response.data)) {
        return {
            data: response.data,
            page: response.page,
            perPage: response.perPage,
            total: response.total,
            totalPages: response.totalPages,
        }
    }

    const body = response.data
    const meta = body.meta

    return {
        data: body.data || [],
        page: meta?.page || response.page || fallbackPage || 1,
        perPage: meta?.perPage || response.perPage || fallbackPerPage || 0,
        total: meta?.totalCount || response.total || 0,
        totalPages: meta?.totalPages || response.totalPages || 0,
    }
}

function normalizeSkillIds(skills: Skill[] | undefined): string[] {
    if (!Array.isArray(skills)) {
        return []
    }

    return skills
        .map(skill => {
            if (typeof skill === 'string') {
                return skill
            }

            if (!skill || typeof skill !== 'object') {
                return ''
            }

            return String((skill as {
                id?: string
                value?: string
            }).id || (skill as {
                id?: string
                value?: string
            }).value || '')
                .trim()
        })
        .filter(Boolean)
}

function createQuery(
    filters: EngagementFilters,
    params: FetchEngagementsParams,
): string {
    const query = new URLSearchParams()

    if (filters.projectId !== undefined) {
        query.set('projectId', String(filters.projectId))
    }

    const statuses = normalizeStatusFilters(filters.status)
    if (statuses.length === 1) {
        query.set('status', toEngagementStatusApi(statuses[0]))
    }

    if (filters.title) {
        query.set('title', filters.title.trim())
    }

    if (filters.sortBy) {
        query.set('sortBy', filters.sortBy)
    }

    if (filters.sortOrder) {
        query.set('sortOrder', filters.sortOrder)
    }

    if (filters.includePrivate) {
        query.set('includePrivate', 'true')
    }

    if (filters.includePrivate && filters.includeLastPaidAt) {
        query.set('includeLastPaidAt', 'true')
    }

    if (filters.excludeTestProjects) {
        query.set('excludeTestProjects', 'true')
    }

    if (Array.isArray(filters.projectIds)) {
        filters.projectIds
            .map(projectId => String(projectId)
                .trim())
            .filter(Boolean)
            .forEach(projectId => {
                query.append('projectIds', projectId)
            })
    }

    if (Array.isArray(filters.countries)) {
        filters.countries
            .map(country => country.trim())
            .filter(Boolean)
            .forEach(country => {
                query.append('countries', country)
            })
    }

    if (Array.isArray(filters.timezones)) {
        filters.timezones
            .map(timezone => timezone.trim())
            .filter(Boolean)
            .forEach(timezone => {
                query.append('timeZones', timezone)
            })
    }

    if (params.page) {
        query.set('page', String(params.page))
    }

    if (params.perPage) {
        query.set('perPage', String(params.perPage))
    }

    return query.toString()
}

/**
 * Normalizes engagement status filters before API requests are built.
 *
 * The work UI can hold one or more selected status labels, while the current
 * engagements API only accepts one status per request. This helper trims empty
 * entries and removes legacy `all` sentinels before request fan-out.
 *
 * @param status single status value or selected status labels from the list filter.
 * @returns non-empty status labels that should be applied to API requests.
 */
function normalizeStatusFilters(status?: string | string[]): string[] {
    const statuses = Array.isArray(status) ? status : [status]
    const normalizedStatuses = statuses
        .map(value => String(value || '')
            .trim())
        .filter(value => !!value && value.toLowerCase() !== 'all')

    return Array.from(new Set(normalizedStatuses))
}

function serializeEngagementPayload(data: EngagementUpsertData): Record<string, unknown> {
    const payload: Record<string, unknown> = {}

    if (data.account !== undefined) {
        payload.account = String(data.account || '')
            .trim()
    }

    if (data.anticipatedStart) {
        payload.anticipatedStart = toEngagementAnticipatedStartApi(data.anticipatedStart)
    }

    if (Array.isArray(data.assignedMemberHandles)) {
        payload.assignedMemberHandles = data.assignedMemberHandles
            .map(handle => String(handle)
                .trim())
            .filter(Boolean)
    }

    if (Array.isArray(data.assignmentDetails)) {
        payload.assignmentDetails = data.assignmentDetails
            .filter(assignment => assignment && assignment.memberHandle)
            .map(assignment => {
                const entry: Record<string, string> = {
                    memberHandle: String(assignment.memberHandle)
                        .trim(),
                }

                if (assignment.agreementRate) {
                    entry.agreementRate = String(assignment.agreementRate)
                        .trim()
                }

                if (assignment.candidateWiproId) {
                    entry.candidateWiproId = String(assignment.candidateWiproId)
                        .trim()
                }

                if (assignment.durationMonths !== undefined && assignment.durationMonths !== '') {
                    const durationMonths = Number(assignment.durationMonths)

                    if (Number.isFinite(durationMonths)) {
                        entry.durationMonths = String(durationMonths)
                    }
                }

                if (assignment.endDate) {
                    entry.endDate = assignment.endDate
                }

                if (assignment.memberId !== undefined) {
                    entry.memberId = String(assignment.memberId)
                }

                if (assignment.otherRemarks) {
                    entry.otherRemarks = String(assignment.otherRemarks)
                        .trim()
                }

                if (assignment.paymentCycle) {
                    entry.paymentCycle = String(assignment.paymentCycle)
                        .trim()
                        .toUpperCase()
                }

                if (assignment.ratePerHour) {
                    entry.ratePerHour = String(assignment.ratePerHour)
                        .trim()
                }

                if (assignment.source) {
                    entry.source = String(assignment.source)
                        .trim()
                        .toUpperCase()
                }

                if (assignment.startDate) {
                    entry.startDate = assignment.startDate
                }

                if (
                    assignment.standardHoursPerDay !== undefined
                    && assignment.standardHoursPerDay !== ''
                ) {
                    const standardHoursPerDay = Number(assignment.standardHoursPerDay)

                    if (Number.isFinite(standardHoursPerDay)) {
                        entry.standardHoursPerDay = String(standardHoursPerDay)
                        entry.standardHoursPerWeek = String(Number((standardHoursPerDay * 5).toFixed(2)))
                    }
                }

                if (
                    assignment.standardHoursPerWeek !== undefined
                    && assignment.standardHoursPerWeek !== ''
                    && entry.standardHoursPerDay === undefined
                ) {
                    const standardHoursPerWeek = Number(assignment.standardHoursPerWeek)

                    if (Number.isFinite(standardHoursPerWeek)) {
                        entry.standardHoursPerWeek = String(standardHoursPerWeek)
                    }
                }

                if (assignment.totalHours !== undefined && assignment.totalHours !== '') {
                    const totalHours = Number(assignment.totalHours)

                    if (Number.isFinite(totalHours) && totalHours > 0) {
                        entry.totalHours = String(totalHours)
                    }
                }

                if (assignment.wiproIdEndDate) {
                    entry.wiproIdEndDate = assignment.wiproIdEndDate
                }

                return entry
            })
    }

    if (data.compensationRange !== undefined) {
        payload.compensationRange = data.compensationRange
    }

    if (Array.isArray(data.countries)) {
        payload.countries = data.countries
    }

    if (data.description !== undefined) {
        payload.description = data.description
    }

    if (data.durationWeeks !== undefined) {
        payload.durationWeeks = Number(data.durationWeeks)
    }

    if (data.isPrivate !== undefined) {
        payload.isPrivate = data.isPrivate
    }

    if (data.projectId !== undefined) {
        payload.projectId = String(data.projectId)
    }

    if (data.receivedDateFromAccount !== undefined) {
        const receivedDateFromAccount = fromEngagementDateInputValue(
            data.receivedDateFromAccount,
        )

        // Backend clears the field when null is sent; empty form values must clear on update.
        payload.receivedDateFromAccount = receivedDateFromAccount
            // eslint-disable-next-line unicorn/no-null
            ?? null
    }

    if (data.requiredMemberCount !== undefined) {
        payload.requiredMemberCount = Number(data.requiredMemberCount)
    }

    if (data.role) {
        payload.role = toEngagementRoleApi(data.role)
    }

    if (data.roleLevel !== undefined) {
        const roleLevel = String(data.roleLevel || '')
            .trim()

        // Backend enum field is cleared with null; empty string fails validation.
        payload.roleLevel = roleLevel
            ? toEngagementRoleLevelApi(roleLevel)
            // eslint-disable-next-line unicorn/no-null
            : null
    }

    const requiredSkills = normalizeSkillIds(data.skills)
    if (requiredSkills.length) {
        payload.requiredSkills = requiredSkills
    }

    if (data.smu !== undefined) {
        payload.smu = String(data.smu || '')
            .trim()
    }

    if (data.spoc !== undefined) {
        payload.spoc = String(data.spoc || '')
            .trim()
    }

    if (data.status) {
        payload.status = toEngagementStatusApi(data.status)
    }

    if (Array.isArray(data.timezones)) {
        payload.timeZones = data.timezones
    }

    if (data.title !== undefined) {
        payload.title = data.title
    }

    if (data.workload) {
        payload.workload = toEngagementWorkloadApi(data.workload)
    }

    return payload
}

function normalizeEngagementRecord(data: Partial<Engagement>): Engagement {
    const normalized = normalizeEngagement({
        ...data,
        anticipatedStart: data.anticipatedStart
            ? fromEngagementAnticipatedStartApi(data.anticipatedStart)
            : 'IMMEDIATE',
        timezones: (data as {
            timeZones?: string[]
        }).timeZones || data.timezones || [],
    })

    return {
        ...normalized,
        assignments: Array.isArray(normalized.assignments)
            ? normalized.assignments
            : [],
    }
}

function normalizeSkillId(skill: Skill | undefined): string {
    if (!skill || typeof skill !== 'object') {
        return ''
    }

    return String(skill.id || '')
        .trim()
}

function getEngagementProjectId(engagement: Engagement): string {
    return String(
        engagement.projectId
        || engagement.project?.id
        || '',
    )
        .trim()
}

function getEngagementProjectName(engagement: Engagement): string {
    return String(
        engagement.projectName
        || engagement.project?.name
        || '',
    )
        .trim()
}

function shouldHydrateEngagementProjectName(engagement: Engagement): boolean {
    const projectId = getEngagementProjectId(engagement)

    if (!projectId) {
        return false
    }

    const projectName = getEngagementProjectName(engagement)

    return !projectName || projectName === projectId
}

const projectNameByProjectId = new Map<string, string>()

async function hydrateEngagementSkillNames(
    engagements: Engagement[],
): Promise<Engagement[]> {
    if (!Array.isArray(engagements) || !engagements.length) {
        return engagements
    }

    const skillIds = Array.from(new Set(
        engagements
            .flatMap(engagement => (Array.isArray(engagement.skills) ? engagement.skills : []))
            .map(skill => normalizeSkillId(skill))
            .filter(Boolean),
    ))

    if (!skillIds.length) {
        return engagements
    }

    try {
        const skills = await fetchSkillsByIds(skillIds)
        const skillNameById = new Map<string, string>(
            skills.map(skill => [
                String(skill.id),
                String(skill.name || skill.id),
            ]),
        )

        return engagements.map(engagement => ({
            ...engagement,
            skills: Array.isArray(engagement.skills)
                ? engagement.skills.map(skill => {
                    const id = normalizeSkillId(skill)

                    if (!id) {
                        return skill
                    }

                    return {
                        ...skill,
                        name: skillNameById.get(id) || skill.name || id,
                    }
                })
                : [],
        }))
    } catch (error) {
        return engagements
    }
}

async function hydrateEngagementProjectNames(
    engagements: Engagement[],
): Promise<Engagement[]> {
    if (!Array.isArray(engagements) || !engagements.length) {
        return engagements
    }

    const projectIds = Array.from(
        new Set(
            engagements
                .filter(shouldHydrateEngagementProjectName)
                .map(getEngagementProjectId)
                .filter(Boolean),
        ),
    )

    if (!projectIds.length) {
        return engagements
    }

    await Promise.all(
        projectIds.map(async projectId => {
            if (projectNameByProjectId.has(projectId)) {
                return
            }

            try {
                const project = await fetchProjectById(projectId)
                const projectName = String(project?.name || '')
                    .trim()

                if (projectName) {
                    projectNameByProjectId.set(projectId, projectName)
                }
            } catch {
                // Project lookup failures should not break engagement loading.
            }
        }),
    )

    return engagements.map(engagement => {
        if (!shouldHydrateEngagementProjectName(engagement)) {
            return engagement
        }

        const projectId = getEngagementProjectId(engagement)
        const projectName = projectNameByProjectId.get(projectId)

        if (!projectName) {
            return engagement
        }

        return {
            ...engagement,
            project: {
                ...(engagement.project || {}),
                id: engagement.project?.id || projectId,
                name: projectName,
            },
            projectName,
        }
    })
}

export async function fetchEngagements(
    filters: EngagementFilters = {},
    params: FetchEngagementsParams = {},
): Promise<FetchEngagementsResponse> {
    const normalizedProjectIds = Array.isArray(filters.projectIds)
        ? filters.projectIds
            .map(projectId => String(projectId)
                .trim())
            .filter(Boolean)
        : undefined

    if (Array.isArray(filters.projectIds) && !normalizedProjectIds?.length) {
        return {
            data: [],
            metadata: {
                page: params.page || 1,
                perPage: params.perPage || 0,
                total: 0,
                totalPages: 0,
            },
        }
    }

    const normalizedStatuses = normalizeStatusFilters(filters.status)

    if (normalizedStatuses.length > 1) {
        const responses = await Promise.all(
            normalizedStatuses.map(status => fetchEngagements({
                ...filters,
                projectIds: normalizedProjectIds,
                status,
            }, params)),
        )
        const data = responses.flatMap(response => response.data)
        const total = responses.reduce(
            (sum, response) => sum + (response.metadata.total || response.data.length),
            0,
        )
        const totalPages = Math.max(
            0,
            ...responses.map(response => response.metadata.totalPages || 0),
        )

        return {
            data,
            metadata: {
                page: params.page || 1,
                perPage: params.perPage || responses[0]?.metadata.perPage || 0,
                total,
                totalPages,
            },
        }
    }

    const query = createQuery({
        ...filters,
        projectIds: normalizedProjectIds,
        status: normalizedStatuses[0],
    }, params)
    const url = query
        ? `${ENGAGEMENTS_API_URL}?${query}`
        : ENGAGEMENTS_API_URL

    try {
        const response = await xhrGetPaginatedAsync<
            Engagement[] | BackendPaginatedResponse<Engagement>
        >(url)

        const normalizedResponse = normalizePaginatedResponse(
            response as EngagementResponse,
            params.page,
            params.perPage,
        )

        const normalizedEngagements = normalizedResponse.data
            .map(item => normalizeEngagementRecord(item))
        const hydratedEngagementsWithSkills = await hydrateEngagementSkillNames(normalizedEngagements)
        const hydratedEngagements = await hydrateEngagementProjectNames(hydratedEngagementsWithSkills)

        return {
            data: hydratedEngagements,
            metadata: {
                page: normalizedResponse.page,
                perPage: normalizedResponse.perPage,
                total: normalizedResponse.total,
                totalPages: normalizedResponse.totalPages,
            },
        }
    } catch (error) {
        throw normalizeError(error, 'Failed to fetch engagements')
    }
}

export async function fetchEngagement(
    engagementId: number | string,
): Promise<Engagement> {
    try {
        const response = await xhrGetAsync<Engagement>(
            `${ENGAGEMENTS_API_URL}/${engagementId}`,
        )

        const normalized = normalizeEngagementRecord(response)
        const [hydratedWithSkills] = await hydrateEngagementSkillNames([normalized])
        const [hydrated] = await hydrateEngagementProjectNames([hydratedWithSkills])

        return hydrated || normalized
    } catch (error) {
        throw normalizeError(error, 'Failed to fetch engagement details')
    }
}

export async function createEngagement(
    data: EngagementUpsertData,
): Promise<Engagement> {
    try {
        const response = await xhrPostAsync<Record<string, unknown>, Engagement>(
            ENGAGEMENTS_API_URL,
            serializeEngagementPayload(data),
        )

        const normalized = normalizeEngagementRecord(response)
        const [hydratedWithSkills] = await hydrateEngagementSkillNames([normalized])
        const [hydrated] = await hydrateEngagementProjectNames([hydratedWithSkills])

        return hydrated || normalized
    } catch (error) {
        throw normalizeError(error, 'Failed to create engagement')
    }
}

export async function updateEngagement(
    engagementId: number | string,
    data: EngagementUpsertData,
): Promise<Engagement> {
    try {
        const response = await xhrPutAsync<Record<string, unknown>, Engagement>(
            `${ENGAGEMENTS_API_URL}/${engagementId}`,
            serializeEngagementPayload(data),
        )

        const normalized = normalizeEngagementRecord(response)
        const [hydratedWithSkills] = await hydrateEngagementSkillNames([normalized])
        const [hydrated] = await hydrateEngagementProjectNames([hydratedWithSkills])

        return hydrated || normalized
    } catch (error) {
        throw normalizeError(error, 'Failed to update engagement')
    }
}

/**
 * Partially updates engagement details.
 *
 * The engagements API currently accepts partial engagement payloads through the
 * `PUT :id` update route rather than a dedicated `PATCH :id` handler.
 *
 * @param engagementId engagement identifier.
 * @param data partial engagement payload.
 * @returns normalized engagement response after the partial update.
 */
export async function partiallyUpdateEngagement(
    engagementId: number | string,
    data: EngagementUpsertData,
): Promise<Engagement> {
    return updateEngagement(engagementId, data)
}

export async function deleteEngagement(engagementId: number | string): Promise<void> {
    try {
        await xhrDeleteAsync(`${ENGAGEMENTS_API_URL}/${engagementId}`)
    } catch (error) {
        throw normalizeError(error, 'Failed to delete engagement')
    }
}

export async function updateEngagementAssignmentStatus(
    engagementId: number | string,
    assignmentId: number | string,
    status: string,
    reason?: string,
): Promise<Assignment> {
    try {
        return xhrPatchAsync(
            `${ENGAGEMENTS_API_URL}/${engagementId}/assignments/${assignmentId}/status`,
            {
                status,
                terminationReason: reason,
            },
        )
    } catch (error) {
        throw normalizeError(error, 'Failed to update assignment status')
    }
}

export async function fetchEngagementFeedback(
    engagementId: number | string,
    assignmentId: number | string,
): Promise<EngagementFeedback[]> {
    try {
        return xhrGetAsync<EngagementFeedback[]>(
            `${ENGAGEMENTS_ROOT_API_URL}/${engagementId}/assignments/${assignmentId}/feedback`,
        )
    } catch (error) {
        throw normalizeError(error, 'Failed to fetch engagement feedback')
    }
}

export async function createEngagementFeedback(
    engagementId: number | string,
    assignmentId: number | string,
    data: CreateEngagementFeedbackPayload,
): Promise<EngagementFeedback> {
    try {
        return xhrPostAsync<CreateEngagementFeedbackPayload, EngagementFeedback>(
            `${ENGAGEMENTS_ROOT_API_URL}/${engagementId}/assignments/${assignmentId}/feedback`,
            data,
        )
    } catch (error) {
        throw normalizeError(error, 'Failed to create engagement feedback')
    }
}

export async function generateEngagementFeedbackLink(
    engagementId: number | string,
    assignmentId: number | string,
    data: FeedbackLinkPayload,
): Promise<{
    expiresAt?: string
    feedbackUrl?: string
    secretToken?: string
}> {
    try {
        return xhrPostAsync<FeedbackLinkPayload, {
            expiresAt?: string
            feedbackUrl?: string
            secretToken?: string
        }>(
            `${ENGAGEMENTS_ROOT_API_URL}/${engagementId}/assignments/${assignmentId}/feedback/generate-link`,
            data,
        )
    } catch (error) {
        throw normalizeError(error, 'Failed to generate feedback link')
    }
}

export async function fetchMemberExperiences(
    engagementId: number | string,
    assignmentId: number | string,
): Promise<MemberExperience[]> {
    try {
        return xhrGetAsync<MemberExperience[]>(
            `${ENGAGEMENTS_ROOT_API_URL}/${engagementId}/assignments/${assignmentId}/experiences`,
        )
    } catch (error) {
        throw normalizeError(error, 'Failed to fetch member experiences')
    }
}

export async function createMemberExperience(
    engagementId: number | string,
    assignmentId: number | string,
    data: CreateMemberExperiencePayload,
): Promise<MemberExperience> {
    try {
        return xhrPostAsync<CreateMemberExperiencePayload, MemberExperience>(
            `${ENGAGEMENTS_ROOT_API_URL}/${engagementId}/assignments/${assignmentId}/experiences`,
            data,
        )
    } catch (error) {
        throw normalizeError(error, 'Failed to create member experience')
    }
}

/**
 * Approved, unpaid hours for a payment period.
 *
 * Only approved entries count, and entries a payment already consumed are excluded from the totals and
 * reported separately, so the operator can see why the total is lower than the member's logged hours.
 */
export async function fetchTimesheetPaymentSummary(
    engagementId: number | string,
    assignmentId: number | string,
    fromDate: string,
    toDate: string,
): Promise<TimesheetPaymentSummary> {
    try {
        const query = new URLSearchParams({ fromDate, toDate })

        return xhrGetAsync<TimesheetPaymentSummary>(
            `${ENGAGEMENTS_ROOT_API_URL}/engagements/${engagementId}/assignments/${assignmentId}`
            + `/timesheets/summary?${query.toString()}`,
        )
    } catch (error) {
        throw normalizeError(error, 'Failed to load approved timesheet hours')
    }
}

/**
 * Approved, unpaid timesheets across the engagements the caller can see, one row per approval.
 */
export async function fetchApprovedTimesheets(
    query: ApprovedTimesheetQuery = {},
): Promise<ApprovedTimesheetListResponse> {
    try {
        const params = new URLSearchParams()

        if (query.engagement?.trim()) {
            params.set('engagement', query.engagement.trim())
        }

        if (query.assignee?.trim()) {
            params.set('assignee', query.assignee.trim())
        }

        if (query.excludeTestProjects) {
            params.set('excludeTestProjects', 'true')
        }

        if (query.page) {
            params.set('page', String(query.page))
        }

        if (query.perPage) {
            params.set('perPage', String(query.perPage))
        }

        const queryString = params.toString()

        return await xhrGetAsync<ApprovedTimesheetListResponse>(
            `${ENGAGEMENTS_ROOT_API_URL}/timesheets/approved${queryString ? `?${queryString}` : ''}`,
        )
    } catch (error) {
        throw normalizeError(error, 'Failed to load approved timesheets')
    }
}

/**
 * Records that a payment consumed these approved entries.
 *
 * Called after the payment exists. A crash in between leaves the entries unmarked, which is visible and
 * recoverable; marking first and then failing to create the payment would strand approved hours as
 * permanently unpayable.
 */
export async function linkTimesheetEntriesToPayment(
    engagementId: number | string,
    assignmentId: number | string,
    entryIds: string[],
    paymentReference: string,
): Promise<void> {
    try {
        await xhrPostAsync<{ entryIds: string[], paymentReference: string }, unknown>(
            `${ENGAGEMENTS_ROOT_API_URL}/engagements/${engagementId}/assignments/${assignmentId}`
            + '/timesheets/entries/payments',
            { entryIds, paymentReference },
        )
    } catch (error) {
        throw normalizeError(error, 'Failed to link the payment to the approved timesheet entries')
    }
}

/**
 * Reconciliation query: which entries a payment reference consumed.
 */
export async function fetchTimesheetEntriesByPaymentReference(
    engagementId: number | string,
    assignmentId: number | string,
    paymentReference: string,
): Promise<TimesheetPaymentEntry[]> {
    try {
        return xhrGetAsync<TimesheetPaymentEntry[]>(
            `${ENGAGEMENTS_ROOT_API_URL}/engagements/${engagementId}/assignments/${assignmentId}`
            + `/timesheets/entries/payments/${encodeURIComponent(paymentReference)}`,
        )
    } catch (error) {
        throw normalizeError(error, 'Failed to reconcile payment entries')
    }
}

/**
 * Lists the managers authorized to approve timesheets on an engagement.
 *
 * These are the same endpoints the Engagements Portal timesheet page uses. There is one manager list
 * behind both apps, which is what makes a change made in either one visible in the other without any
 * synchronization between them.
 */
export async function fetchEngagementManagers(
    engagementId: number | string,
): Promise<EngagementManager[]> {
    try {
        return xhrGetAsync<EngagementManager[]>(
            `${ENGAGEMENTS_ROOT_API_URL}/engagements/${engagementId}/managers`,
        )
    } catch (error) {
        throw normalizeError(error, 'Failed to fetch engagement managers')
    }
}

/**
 * Grants a member timesheet approval authority on an engagement.
 *
 * Keyed on the member's user id; the handle and name come along as display values from the member
 * picker, which saves the API a member lookup. A member already assigned is rejected server-side,
 * and re-assigning someone previously removed reactivates their record rather than creating a
 * duplicate.
 */
export async function assignEngagementManager(
    engagementId: number | string,
    manager: AssignEngagementManagerPayload,
): Promise<EngagementManager> {
    try {
        return xhrPostAsync<AssignEngagementManagerPayload, EngagementManager>(
            `${ENGAGEMENTS_ROOT_API_URL}/engagements/${engagementId}/managers`,
            manager,
        )
    } catch (error) {
        throw normalizeError(error, 'Failed to assign engagement manager')
    }
}

/**
 * Revokes a manager's timesheet approval authority.
 *
 * Soft-deleted server-side, so approvals this manager already made keep their attribution.
 */
export async function removeEngagementManager(
    engagementId: number | string,
    managerUserId: string,
): Promise<void> {
    try {
        await xhrDeleteAsync(
            `${ENGAGEMENTS_ROOT_API_URL}/engagements/${engagementId}/managers/${managerUserId}`,
        )
    } catch (error) {
        throw normalizeError(error, 'Failed to remove engagement manager')
    }
}
